import _ from "lodash";
import jwt_decode from "jwt-decode";
import AppSettings from "../config";
import * as Auth from "../lib/auth";
import { JWTPayload } from "../types/auth/JWTPayload";
import { Roles } from "../types/auth/Role";

function getAccessToken(): JWTPayload | null {
  const rawToken = Auth.getAccessToken();
  if (!rawToken) return null;
  const decodedToken = jwt_decode<JWTPayload>(rawToken);
  return decodedToken;
}

function isTokenExpired(): boolean {
  const token = getAccessToken();
  const exp = token?.exp ?? 0;
  return exp * 1000 < Date.now();
}

function isLoggedIn(): boolean {
  const token = getAccessToken();
  if (!token || isTokenExpired()) return false;

  const siteName: string | null = token?.data?.site?.name ?? null;
  return Auth.checkSiteMatch(siteName);
}

function isInternalUser(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  const permissions = token?.data?.permissions;
  return permissions === Roles.INTERNAL_ADMIN;
}

function isCitizen(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  return token?.data?.user?.isEmployee !== true;
}

function isSuperAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.SUPER_ADMIN);
}

function isDepartmentAdmin(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.CRM_DEPARTMENT_ADMIN);
}

function isCrmAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.CRM_ADMIN);
}

function isCrmUser(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.CRM_USER);
}

function isCeAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.CE_ADMIN);
}

function isCeUser(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.CE_USER);
}

function isCommunicationsAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.COMMUNICATION_ADMIN);
}

function isCommunicationsUser(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.COMMUNICATION_USER);
}

function isPermitsAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.PERMIT_ADMIN);
}

function isPermitsUser(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.PERMIT_USER);
}

function isInspectionsAdmin(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.INSPECTIONS_ADMIN);
}

function isInspectionsUser(): boolean {
  if (isInternalUser()) {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const roles = token?.data?.roles ?? [];
  return roles.includes(Roles.INSPECTIONS_USER);
}

function getUserEmail(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user?.email;
}

function getUser() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user;
}

function getUserUsername() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user?.username;
}

export function getUserFirstName() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user?.firstName;
}

export function getUserLastName() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user?.lastName;
}

function getUserId() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.user?.id;
}

function permissions() {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.permissions;
}

function postResetPassword(state: any) {
  return state.identity.postResetPassword;
}

function postVerify(state: any) {
  return state.identity.postVerify;
}

function postSendWelcome(state: any) {
  return state.identity.postSendWelcome;
}

function postForgotPassword(state: any) {
  return state.identity.postForgotPassword;
}

function postLogin(state: any) {
  return state.identity.postLogin;
}

function postSignup(state: any) {
  return state.identity.postSignup;
}

function putProfile(state: any) {
  return state.identity.putProfile;
}

function getLegacyExchangeUrl() {
  return (site: string, remember?: boolean, redirect?: string) => {
    const decoded = getAccessToken();
    const phpSessionId = _.get(decoded, "data.legacy.php_session_id");
    const legacyHost = redirect?.match(/m\.((?:staging\.|preprod\.)?govoutreach\.com)/) // Is a redirect to mobile version of site
      ? AppSettings.GOGOV_WEB_URL.replace("user.", "m.")
      : AppSettings.GOGOV_WEB_URL;

    const redirectComponents = new URL(redirect || `${legacyHost}/${site}/internal.php`);

    const exchangeHost =
      redirectComponents.host === window.location.host ? new URL(legacyHost).host : redirectComponents.host;
    const encodedRedirect = encodeURIComponent(redirectComponents.href);
    return `https://${exchangeHost}/exchange/exchange.php?redirect=${encodedRedirect}&phpsessionid=${phpSessionId}&rememberme=${remember}&site=${site}`;
  };
}

function getSupportUrl() {
  return (site: string, remember: boolean | undefined, redirect?: string | undefined) => {
    const decoded = getAccessToken();
    const phpSessionId = _.get(decoded, "data.legacy.php_session_id");
    const redirectComponents = new URL(redirect || `${AppSettings.GOGOV_WEB_URL}/${site}/support.php`);
    const exchangeHost =
      redirectComponents.host === window.location.host
        ? new URL(AppSettings.GOGOV_WEB_URL).host
        : redirectComponents.host;
    const encodedRedirect = encodeURIComponent(redirectComponents.href);
    return `https://${exchangeHost}/exchange/exchange.php?redirect=${encodedRedirect}&phpsessionid=${phpSessionId}&rememberme=${remember}&site=${site}`;
  };
}

const getLoginHistory = (id: string) => (state: any) => {
  return state.identity.getLoginHistory[id];
};

const getUserSite = (): string | null => {
  const token = getAccessToken();
  if (!token) return null;
  return token?.data?.site?.name;
};

export const identitySelectors = {
  isInternalUser,
  isTokenExpired,
  isLoggedIn,
  isSuperAdmin,
  isCrmAdmin,
  isCrmUser,
  isCeAdmin,
  isCeUser,
  isCommunicationsAdmin,
  isCommunicationsUser,
  isPermitsAdmin,
  isPermitsUser,
  isDepartmentAdmin,
  isCitizen,
  getUserId,
  getUserEmail,
  getUserUsername,
  getUser,
  getUserFirstName,
  getUserLastName,
  getLegacyExchangeUrl,
  getSupportUrl,
  permissions,
  postResetPassword,
  postForgotPassword,
  postLogin,
  postSignup,
  postVerify,
  postSendWelcome,
  putProfile,
  getLoginHistory,
  getUserSite,
  isInspectionsAdmin,
  isInspectionsUser,
};
export default identitySelectors;
